// Support — the field screen. Someone phones and says "I can't get in."
//
// The whole flow is one input: type the email they gave you, get the account,
// its org, its session state, its recent work, and a plain-language read on
// what is actually wrong — including the case where there is no account, which
// is the most common one a provisioning-only product produces.
//
// Everything else here exists to shorten the call: the fix for each diagnosis
// is a button on the same screen, not a trip through three other tabs.

import React from 'react';
import { api, errorText } from '../api';
import { useAsync } from '../useAsync';
import { navigate } from '../router';
import { FLAG_COPY, ago, jobTone, stamp } from '../format';
import UserSheet from '../ui/UserSheet';
import {
  Button, Card, Empty, ErrorState, Icon, KV, Note, PageHead, Pill, Row, Search,
  Skeleton,
} from '../ui/primitives';

const PLAYBOOK = [
  {
    title: '“Not provisioned”',
    body: 'Mono has no self-signup. Open the right org and provision the exact email they sign in to Google with.',
  },
  {
    title: '“Account conflict” at sign-in',
    body: 'Their email is bound to a different Google identity (usually personal vs work). Unbind Google on their account; the next sign-in re-binds.',
  },
  {
    title: 'Signed out repeatedly',
    body: 'Check whether the user or the org is deactivated — both revoke sessions. If neither, force sign-out and have them sign in once cleanly.',
  },
  {
    title: 'Lost or shared device',
    body: 'Force sign-out revokes every refresh session. Their access token still works for up to 15 minutes — deactivate instead if it must stop now.',
  },
];

export default function Support({ query }) {
  const [email, setEmail] = React.useState(query.email || '');
  const [submitted, setSubmitted] = React.useState(query.email || '');
  const [openUser, setOpenUser] = React.useState(null);

  const { data, error, loading, reload } = useAsync(
    () => api.supportLookup(submitted), [submitted], { skip: !submitted });

  const search = () => setSubmitted(email.trim());
  const flags = data?.flags || [];

  return (
    <>
      <PageHead title="Support" sub="Look up an account by the email they gave you" />

      <div className="stack" style={{ marginBottom: 16 }}>
        <form onSubmit={(e) => { e.preventDefault(); search(); }}>
          <Search value={email} onChange={setEmail} placeholder="name@company.com"
                  type="email" inputMode="email" enterKeyHint="search"
                  autoComplete="off" />
        </form>
        <Button variant="primary" block onClick={search} loading={loading}
                disabled={!email.trim()}>
          <Icon name="search" size={16} />Look up
        </Button>
      </div>

      {error && <ErrorState error={error} text={errorText(error)} onRetry={reload} />}
      {loading && !data && <Skeleton rows={4} />}

      {data && data.found === false && (
        <div className="stack">
          <Note tone="danger" title="No account for that email">
            {FLAG_COPY.not_provisioned.hint}
          </Note>
          {data.suggestions?.length > 0 && (
            <Card title="Similar emails" bodyless>
              <div className="list">
                {data.suggestions.map((u) => (
                  <Row key={u.id} onClick={() => { setEmail(u.email); setSubmitted(u.email); }}>
                    <div className="col" style={{ flex: 1 }}>
                      <span className="mono truncate" style={{ fontSize: 12.5 }}>{u.email}</span>
                      <span className="dim" style={{ fontSize: 11 }}>{u.org_name}</span>
                    </div>
                  </Row>
                ))}
              </div>
            </Card>
          )}
          <Button block onClick={() => navigate('/orgs')}>
            Go to Orgs to provision them
          </Button>
        </div>
      )}

      {data && data.found && (
        <div className="stack">
          <Card title="Account" action={
            <Button small onClick={() => setOpenUser(data.user.id)}>Actions</Button>
          }>
            <div className="stack">
              <div className="row row-wrap" style={{ gap: 6 }}>
                <Pill tone={data.user.is_active ? 'ok' : 'danger'}>
                  {data.user.is_active ? 'active' : 'deactivated'}
                </Pill>
                <Pill tone="accent">{data.user.role}</Pill>
                <Pill tone={data.user.sso_bound ? '' : 'warn'}>
                  {data.user.sso_bound ? 'google bound' : 'not bound'}
                </Pill>
                <Pill tone={data.sessions.active_families > 0 ? 'ok' : ''}>
                  {data.sessions.active_families} session{data.sessions.active_families === 1 ? '' : 's'}
                </Pill>
              </div>
              <KV items={[
                ['Name', data.user.display_name || null],
                ['Email', <span key="e" className="mono" style={{ fontSize: 12 }}>{data.user.email}</span>],
                ['Org', data.org
                  ? <button key="o" className="btn ghost small" style={{ padding: 0 }}
                            onClick={() => navigate(`/orgs/${data.org.id}`)}>
                      {data.org.name}{data.org.is_active ? '' : ' (deactivated)'}
                    </button>
                  : null],
                ['Last login', data.sessions.last_login_at
                  ? `${ago(data.sessions.last_login_at)} · ${stamp(data.sessions.last_login_at)}`
                  : 'never'],
              ]} />
            </div>
          </Card>

          {/* The diagnosis — what is actually wrong, and what to do. */}
          {flags.filter((f) => FLAG_COPY[f]).length > 0 && (
            <div className="stack">
              <h2>Diagnosis</h2>
              {flags.filter((f) => FLAG_COPY[f]).map((f) => (
                <Note key={f} tone={FLAG_COPY[f].tone} title={FLAG_COPY[f].label}>
                  {FLAG_COPY[f].hint}
                </Note>
              ))}
            </div>
          )}

          {flags.filter((f) => FLAG_COPY[f]?.tone).length === 0 && (
            <Note tone="ok" title="Nothing obviously wrong">
              This account is active, bound and in an active org. If they still
              cannot work, check Monitor for job failures around the time they
              report.
            </Note>
          )}

          {data.jobs.recent.length > 0 && (
            <Card title="Recent jobs" bodyless>
              <div className="list">
                {data.jobs.recent.slice(0, 6).map((j) => (
                  <div key={j.job_id} className="list-row">
                    <Pill tone={jobTone(j.status)}>{j.status}</Pill>
                    <div className="col" style={{ flex: 1 }}>
                      <span className="truncate" style={{ fontSize: 12.5 }}>{j.kind}</span>
                      <span className="dim mono truncate" style={{ fontSize: 10.5 }}>
                        {j.job_id}
                      </span>
                    </div>
                    <span className="dim nowrap" style={{ fontSize: 11 }}>{ago(j.created_at)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {data.recent_events.length > 0 && (
            <Card title="Recent activity" bodyless>
              <div className="list">
                {data.recent_events.slice(0, 10).map((e) => (
                  <div key={e.id} className="list-row">
                    <div className="col" style={{ flex: 1 }}>
                      <span className="mono truncate" style={{ fontSize: 12 }}>{e.event_type}</span>
                      <span className="dim" style={{ fontSize: 11 }}>{stamp(e.ts)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {!submitted && (
        <div className="stack">
          <Card title="Playbook">
            <div className="stack" style={{ gap: 13 }}>
              {PLAYBOOK.map((p) => (
                <div key={p.title} className="col" style={{ gap: 3 }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{p.title}</span>
                  <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>{p.body}</span>
                </div>
              ))}
            </div>
          </Card>
          <Empty title="Start with an email">
            Everything about an account — org, sessions, jobs, history — comes
            back in one lookup.
          </Empty>
        </div>
      )}

      {openUser && (
        <UserSheet userId={openUser} onClose={() => setOpenUser(null)} onChanged={reload} />
      )}
    </>
  );
}
